// fx 'extrude' — LIVE 3D extrusion of real, editable text (kind:'source').
//
// The step REPLACES the glyph raster with a WebGL bake of the same words:
// opentype outlines → ExtrudeGeometry → PBR materials → lighting preset →
// the shared offscreen renderer (lib/text3d — the exact visual family the
// Styles Explorer's baked 3D uses). The object stays a stock fabric Textbox:
// still typed into, still serialized as plain JSON; only pixels are 3D.
//
// Owner's signature option: plate — a die-cut "sticker" slab standing proud
// of the poster with the letters extruding further out of it (plateGeometry
// builds the slab from the dilated text silhouette; letters sit ON the slab,
// shared lights, one object). "Theoretically we could have an extruding
// sticker with extruding text from the extruding sticker." — that is this.
//
// Boot rule: this module is imported at editor boot (registry needs it), so
// it must stay pure JS — three.js/opentype load ONLY inside applyAsync via
// the dynamic liveExtrude import.
//
// Live-text mapping notes:
// - Fonts (GlyphTrace lane, 2026-07-20 — owner: "the font always reverts to
//   one font… maintain the font(s) originally chosen in the 2d text"): the
//   bake now renders the TRUE editor fonts. buildTextLayoutSnapshot hands
//   liveExtrude fabric's own layout metrics (__charBounds pen positions,
//   line baselines, per-char resolved styles) and glyph outlines are traced
//   from the browser's own rendering (lib/text3d/glyphTrace.js) — mixed
//   per-char fontFamily/Weight/Style/Size runs survive 3D-ification. The
//   curated TTF list (fonts3d.pickFont3dFamily) remains ONLY as the
//   fallback: per-run when a single trace fails, whole-bake when no
//   snapshot exists (text-on-path, RTL) — the pre-lane "closest family"
//   behavior, never a crash.
// - PAINT (3D-controls lane, 2026-07-17 — "we still need the ability to do
//   anything to extruded text"): the whole fill surface now maps into the
//   bake instead of collapsing to a solid:
//     solid    — rides obj.fill exactly as before (recolor → re-bake);
//     gradient — the data.textFill recipe (else the live fabric Gradient's
//                stops) is handed to liveExtrude as faceFill and rendered
//                onto the letter FACES as a real canvas texture; walls/back
//                keep a darker solid derived from the ramp's MID color;
//     texture  — the data.textFill {type:'texture', id, scale} recipe wraps
//                the letters in the manifest tile (front cap in shape space,
//                walls triplanar), repeat calibrated to the 2D pattern
//                density. The step's own archetype textureId still wins —
//                it is the more deliberate 3D material choice;
//     per-char — Emphasize/phonics fill overrides ride charFills into
//                per-color material blocks: an emphasized red word is red
//                3D letters (per-char bold/size/script stay 2D-only).
// - Outline (the stroke-slot effect) is re-expressed as a flat 2D RIM around
//   the baked silhouette — see applyRimUnderlay. Same composition philosophy
//   as the consumed bevel: the NATIVE effect's look, rendered by the source.
// - depth 0-100 maps onto the baked pipeline's world range (TEXT3D depth
//   4-60) so live and modal 3D read as one family.
//
// UNIVERSAL-EFFECTS U3 (2026-07-21, owner: "apply all the same effects to
// shapes and icons"): the step now extrudes SHAPES and ICONS through the
// silhouetteOf abstraction — the U1 object-silhouette contract made 3D:
//   shapes — EXACT VECTORS: shapeGeomOf snapshots the fabric geometry
//     (rounded-rect kappa corners, circle/ellipse arcs, polygon points,
//     path commands) and text3d/silhouette3d re-cuts it as THREE Shapes
//     through the glyph work's path→Shape machinery (winding/holes
//     normalized). No rastering anywhere — a 3D circle sweeps smooth at
//     print zoom under the tessellation error law.
//   icons — the TRACED silhouette: fxContourOutline's sub-pixel extraction
//     (reused verbatim, cache and all) walks the U1 silhouette variant into
//     object-unit contours; silhouette3d nests them into solids + holes
//     (the GlyphTrace pattern at icon scale).
// The whole 3D feature set inherits by construction: depth/bevel mapping,
// finishes/archetypes, sideFill, lightPreset + poster light, rx/ry + the
// joystick (its attach law keys off this step's presence, kind-blind),
// sticker plate (plateGeometry builds from the same placed shapes), export
// pre-await, viewport patches, tessellation law. COLOR LAW: a shape's solid
// fill dyes the faces (opaqueness law); a gradient fill rides the U1
// face-texture path (ramp on the front caps, walls at the mid tone); a
// hollow-parked recipe is honored (the fill-hostage law's memory); an
// icon's TINT is its color identity (iconColor — untinted multicolor SVGs
// sample their raster's alpha-weighted mean). Sides recolor via sideFill
// exactly as on text. Degrades stay honest: a null silhouette (Line, blank
// icon, tracer hiccup) keeps the flat raster — the seam's documented
// fallback. Size law: u = min(w,h)/100 (the seam's own convention), so
// depth/bevel/plate proportions survive a corner-drag resize.
//
// BEVEL COMPOSITION (owner 2026-07-16: "the bevel should survive the
// 3d-ification"): this step declares consumes:['bevel'] — while extrude is
// active, a sibling 'bevel' step stays in data.fx with its original params
// but the seam never runs the 2D filter; instead applyAsync reads that
// step's params and re-expresses them as the 3D geometry's EDGE TREATMENT
// (a real profile cut into the letters, lit by the 3D lights). Since the
// dome-parity lane (2026-07-19 — owner: "the bevel width is not changing
// anything" in 3D) the mapping carries the WHOLE 2D mental model, not just
// depth:
//   depth (0-100)   → prominence/steepness (bevelThickness scaling);
//   size  (0-100)   → geometric extent (bevelSize — how far the treatment
//                     eats into the face, thickness scaled alongside);
//   technique       → profile: 'smooth' = curved quarter-round, 'chisel' =
//                     straight 1-segment chamfer (crisp mitre);
// with liveExtrude capping thickness against the slab depth so a maxed
// bevel rounds without self-intersecting, front AND back rims identically
// (ExtrudeGeometry bevels both ends — the walls meet both caps through the
// same profile). Drop extrude back to 0 and the bevel step — untouched all
// along — runs live 2D again, exactly as saved. When no bevel step rides
// along, the step's own bevelRounding param (explorer presets, older
// designs) keeps driving depth+width together, unchanged in spirit.

import { registerFx } from './fxRegistry';
// 3D faces cannot carry paint alpha (three.js Color drops it, warning) —
// strip it at the derivation seam (PaintPicker v2 paint-opacity law).
import { normalizeOpaqueHex } from '../../primitives/colorMath.mjs';
// Sibling-step default only (pure JS, boot-safe, no cycle — fxBevel never
// imports this module): a bevel step saved without size composes at the same
// width the 2D filter would render it with.
import { FX_BEVEL_DEFAULTS } from './fxBevel.js';
// Pure raster helpers (no fabric/three) — the outline rim reuses the die-cut
// closing so the band bridges glyph pockets exactly like the 2D border does.
import { bridgeDiecutPockets, diecutCloseRadiusPx } from '../raster/diecutClose.js';
import { getPosterLightFor } from './fxPosterLight.js';
import { getRenderCaps } from '../renderCaps.js';
// U3 (universal-effects): kind routing + the icon tracer. Both pure JS,
// boot-safe — objectKindOf is the matrix's classifier, and the tracer entry
// only runs inside applyAsync where the seam's env carries the variant hook.
import { objectKindOf } from './capabilityMatrix.js';
import { traceObjectSilhouette } from './fxContourOutline.js';

export const FX_EXTRUDE_DEFAULTS = {
  depth: 55, // slider 0-100 → world 4-60 (0 removes the step — see isEmpty)
  bevelRounding: 22, // 0-100 → edge rounding (0 = crisp square cut)
  archetype: 'satin', // "Finish" in the sidebar: matte|satin|candy-gloss|metal-foil
  textureId: null, // manifest texture id for the letter faces (optional)
  sideFill: null, // walls + back cap: null = match the letters (today's
  // derivation), '#rrggbb' = solid dye, else a manifest texture id (triplanar
  // walls, same manifest path as the face/plate). Front face untouched.
  lightPreset: 'studio',
  // 'custom' = the preset rig as-is; 'poster' = the preset's KEY light is
  // re-aimed at the document's shared posterLight (fill/rim keep the preset's
  // character — lighting3d.posterKeyDirection documents the mapping).
  lightMode: 'custom',
  rx: -8, // gentle hero tilt (degrees, camera orbit — TEXT3D_RANGES)
  ry: 14,
  plate: null, // {enabled, width 0-100, depth 0-100, fill hex|textureId|null}
};

export const FX_EXTRUDE_PLATE_DEFAULTS = {
  enabled: false,
  width: 50, // 0-100 → die-cut border reach (world 2-50 around the letters)
  depth: 40, // 0-100 → plate depth as a fraction of letter depth (capped 0.9)
  fill: null, // null = match the letters' color; '#rrggbb' or a texture id
};

// VOLUME STYLES (Depth Everywhere T1, 2026-07-22 — CONCEPT-3d-letter-volumes
// "balloons in the shape of letters"): the 3D step's geometry PRODUCER swaps
// behind volumeStyle while everything downstream (materials family, lights,
// camera, plate, joystick, viewport patches, export pre-await, print tiles)
// consumes the composed scene unchanged. This object is the T3 UI surface
// (ranges/labels source of truth):
//   volumeStyle 'solid'  — today's ExtrudeGeometry pipeline. NEVER stored
//                (noise-drop law): absent key = pre-T1 JSON = byte-identical
//                render AND seam hash (params ride stepsSig whole).
//              'puffy'  — Tier-1 rounded-rim inflation (lib/geo/puffySolid;
//                engine 'auto': exact minkowski rim for hole-free ink,
//                levelSet plateau field where counters would explode the
//                convex decomposition). The puffy-sticker / marshmallow read.
//              'pillow' — Tier-2 true balloon membrane (lib/geo/pillowSolid,
//                poisson profile ∇²u=−4 → z=H·(u/umax)^p — C¹ smooth on any
//                stroke width; capsule's thin-stroke crease is the documented
//                trap, never shipped).
//   volumeRound 0-100 — edge/inflation roundness. puffy: rim roundover
//                radius (0 ≈ crisp die-cut edge, 100 = full marshmallow,
//                physical caps + the thin-stroke guard live in the builder);
//                pillow: membrane inflation (0 = soft shallow dome p≈0.95,
//                100 = tight fully-inflated balloon p=0.5).
//   volumeBack 'flat' — poster law default: the back rests on the poster
//                plane. 'round' — the full balloon (front profile mirrored).
//                TOTAL depth stays the depth slider's world value either way
//                (bounds() headroom and the plate fraction inherit as-is).
//   volumeMaterial — T3 SEAM, stub this lane: accepted + stored ('default'
//                dropped), but ONLY 'default' is consumed — the volume wears
//                the object's own face-fill resolution (liveExtrude's
//                volume-material collapse). T3 wires volumeMaterials.js
//                preset ids (mylar/latex/candy/clay/neon/gummyFaux) through
//                this key with renderCaps tier gating.
// Composed-bevel note: while a volume style is active the sibling 'bevel'
// step (still consumed — the 2D filter must not double-draw) maps onto
// NOTHING: the volume's own rim law owns the edge. Drop back to 'solid' and
// the 3D bevel mapping (or 2D bevel at depth 0) returns untouched.
export const FX_EXTRUDE_VOLUME_DEFAULTS = {
  style: 'solid',
  round: 50,
  back: 'flat',
  material: 'default',
};

// World-unit mapping shared with bounds() and liveExtrude (keep in sync).
export const EXTRUDE_WORLD = {
  minDepth: 4,
  maxDepth: 60, // TEXT3D_RANGES.depth
  minBorder: 2,
  maxBorder: 50,
  maxPlateFraction: 0.9, // plate always shallower than the letters
};

const clamp = (v, min, max, fb) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return fb;
  return Math.min(max, Math.max(min, n));
};

const ARCHETYPES = ['matte', 'satin', 'metal-foil', 'candy-gloss'];
const LIGHTS = ['studio', 'sunset-rim', 'neon-dark', 'soft-top'];

function sanitizePlate(p) {
  if (!p || typeof p !== 'object') return null;
  const d = FX_EXTRUDE_PLATE_DEFAULTS;
  const plate = {
    enabled: !!p.enabled,
    // width runs to 200 (owner, 2026-07-16): /100 in the world mapping is the
    // calibration point, not a ceiling — 100 keeps its exact pre-change reach
    // and values beyond extrapolate linearly.
    width: clamp(p.width, 0, 200, d.width),
    depth: clamp(p.depth, 0, 100, d.depth),
    fill: typeof p.fill === 'string' && p.fill ? p.fill : null,
  };
  // A disabled default-shaped plate is pure noise in the JSON — drop it.
  if (!plate.enabled && plate.width === d.width && plate.depth === d.depth && !plate.fill) {
    return null;
  }
  return plate;
}

function sanitize(p) {
  const src = p || {};
  const d = FX_EXTRUDE_DEFAULTS;
  const out = {
    depth: clamp(src.depth, 0, 100, d.depth),
    bevelRounding: clamp(src.bevelRounding, 0, 100, d.bevelRounding),
    archetype: ARCHETYPES.includes(src.archetype) ? src.archetype : d.archetype,
    textureId: typeof src.textureId === 'string' && src.textureId ? src.textureId : null,
    lightPreset: LIGHTS.includes(src.lightPreset) ? src.lightPreset : d.lightPreset,
    lightMode: src.lightMode === 'poster' ? 'poster' : 'custom',
    rx: clamp(src.rx, -35, 35, d.rx), // TEXT3D_RANGES.rx / .ry
    ry: clamp(src.ry, -45, 45, d.ry),
  };
  // sideFill mirrors plate's noise-drop: absent = 'match the letters', so the
  // key only exists when it means something — old params keep their exact
  // JSON (and their seam hash: params ride stepsSig whole).
  const sideFill = typeof src.sideFill === 'string' && src.sideFill ? src.sideFill : null;
  if (sideFill) out.sideFill = sideFill;
  const plate = sanitizePlate(src.plate);
  if (plate) out.plate = plate;
  // Volume styles (T1): noise-drop law — the keys exist ONLY while a volume
  // style is active, so every pre-T1 params object keeps its exact JSON and
  // therefore its exact seam hash (the solid zero-regression cell). 'solid'
  // is the absent default, never stored; volumeRound persists alongside the
  // style (the T3 slider's state); volumeBack only when 'round';
  // volumeMaterial only when a non-'default' id was chosen (stored for T3,
  // unconsumed this lane).
  if (src.volumeStyle === 'puffy' || src.volumeStyle === 'pillow') {
    out.volumeStyle = src.volumeStyle;
    out.volumeRound = clamp(src.volumeRound, 0, 100, FX_EXTRUDE_VOLUME_DEFAULTS.round);
    if (src.volumeBack === 'round') out.volumeBack = 'round';
    if (typeof src.volumeMaterial === 'string' && src.volumeMaterial
      && src.volumeMaterial !== FX_EXTRUDE_VOLUME_DEFAULTS.material) {
      out.volumeMaterial = src.volumeMaterial;
    }
  }
  return out;
}

/** World depths implied by the params — bounds() and applyAsync agree. */
export function extrudeWorldDepths(p) {
  const letters = EXTRUDE_WORLD.minDepth
    + (clamp(p.depth, 0, 100, 0) / 100) * (EXTRUDE_WORLD.maxDepth - EXTRUDE_WORLD.minDepth);
  const plateOn = !!(p.plate && p.plate.enabled);
  const plate = plateOn
    ? letters * Math.min(EXTRUDE_WORLD.maxPlateFraction, clamp(p.plate.depth, 0, 100, 40) / 100)
    : 0;
  const border = plateOn
    ? EXTRUDE_WORLD.minBorder
      + (clamp(p.plate.width, 0, 200, 50) / 100) * (EXTRUDE_WORLD.maxBorder - EXTRUDE_WORLD.minBorder)
    : 0;
  return { letters, plate, border };
}

/**
 * Declared output bounds (object units, per side, per axis). The bake is
 * front-face-normalized: the projected front cap maps onto the object's
 * width, so everything BEYOND the front face — extrusion walls swung out by
 * rotation, the plate border, perspective divergence of near edges — reaches
 * past the tight box. This is a deliberate over-estimate (~25% headroom):
 * clipping is a bug, slack is just cache pixels. 1 world unit ≈ u object
 * units (TEXT3D_SIZE 100 ↔ fontSize = 100·u).
 */
function bounds(p, { u, w, h, obj }) {
  if (!(p.depth > 0)) return { marginX: 0, marginY: 0 };
  // Outline rim (see applyRimUnderlay): the flat band reaches strokeWidth/2
  // past the baked silhouette on every side — a NATIVE-prop spill the params
  // can't see, hence geo.obj. Sub-pixel reaches still render (the pad guard
  // is in device px), so budget any positive reach.
  const rimU = outlineReachOf(obj);
  const { letters, plate, border } = extrudeWorldDepths(p);
  const depthAll = letters + plate + 10; // + bevel/edge headroom, world units
  const rx = (Math.min(60, Math.abs(p.rx || 0)) * Math.PI) / 180;
  const ry = (Math.min(60, Math.abs(p.ry || 0)) * Math.PI) / 180;
  // Front-face normalization: turning the slab shrinks the projected front
  // cap (cos × the far side receding in perspective); the bake scales back
  // up by the inverse, growing everything with it.
  const invCosY = 1 / Math.max(0.5, Math.cos(ry));
  const invCosX = 1 / Math.max(0.5, Math.cos(rx));
  // Perspective slack: fov 30° + fit margin 1.08. Near-edge magnification of
  // a wide object turned by ry reads as extra HEIGHT on the near side (and
  // the transposed case for rx) — the cross terms, COMPOUNDED by the
  // normalization above (measured at the range extremes: the bake canvas can
  // reach ~0.7·(w/2) vertically at ry 45°; 0.85·invCos keeps headroom).
  const persp = 0.15;
  const crossY = (w / 2) * Math.sin(ry) * 0.85 * invCosY;
  const crossX = (h / 2) * Math.sin(rx) * 0.85 * invCosX;
  const marginX = u * (depthAll * Math.sin(ry) + depthAll * 0.4 + border + 14)
    + (w / 2) * (invCosY - 1 + persp) + crossX;
  const marginY = u * (depthAll * Math.sin(rx) + depthAll * 0.4 + border + 14)
    + (h / 2) * (invCosX - 1 + persp) + crossY;
  return { marginX: marginX * 1.25 + rimU, marginY: marginY * 1.25 + rimU };
}

/** The object's fill as a solid CSS color for the 3D material — the walls'
 * base tone and the plate's 'match letters' color. For gradients this is the
 * ramp's MID blend (not the old first-stop guess): the faces carry the real
 * ramp via faceFill, and the walls read as "the middle of that material". */
function materialColorOf(obj, faceFill, midColorOf) {
  if (faceFill && Array.isArray(faceFill.stops) && faceFill.stops.length >= 2 && midColorOf) {
    try { return midColorOf(faceFill.stops); } catch (_) { /* fall through */ }
  }
  const f = obj.fill;
  if (typeof f === 'string' && f && f !== 'transparent') return f;
  const stop = f && f.colorStops && f.colorStops[0];
  if (stop && typeof stop.color === 'string') return stop.color;
  // Live fill held hostage (hollow/trace blank it to 'transparent') — the
  // recipe on data.textFill still knows the teacher's color.
  const tf = obj.data && obj.data.textFill;
  if (typeof tf === 'string' && tf && tf !== 'transparent') return tf;
  if (tf && tf.type === 'solid' && typeof tf.color === 'string') return tf.color;
  return '#3b82f6';
}

/** The object's fill RECIPE for the letter faces. data.textFill (the
 * inspector's source of truth) when present; else a best-effort recipe from
 * the live fabric Gradient (objects that predate the inspector keep their
 * ramp — the angle is not recoverable, same trade the 2D mirror makes).
 * Solids return null: the plain `color` path already covers them. */
function faceFillOf(obj) {
  const tf = obj.data && obj.data.textFill;
  if (tf && typeof tf === 'object') {
    if (tf.type === 'texture' && tf.id) return { type: 'texture', id: tf.id, scale: tf.scale ?? 1 };
    if ((tf.type === 'linear-gradient' || tf.type === 'radial-gradient')
      && Array.isArray(tf.stops) && tf.stops.length >= 2) {
      return {
        type: tf.type,
        angle: tf.angle ?? 0,
        stops: tf.stops.map((s) => ({ offset: s.offset, color: s.color })),
      };
    }
    return null;
  }
  const live = obj.fill;
  if (live && Array.isArray(live.colorStops) && live.colorStops.length >= 2) {
    return {
      type: live.type === 'radial' ? 'radial-gradient' : 'linear-gradient',
      angle: 0,
      stops: live.colorStops.map((s) => ({ offset: s.offset, color: s.color })),
    };
  }
  return null;
}

/**
 * U3 — plain-data geometry snapshot of a fabric SHAPE for the exact-vector
 * silhouette (text3d/silhouette3d mirrors each class's own _render command
 * stream from this). READ ONLY, no fabric/three imports — boot-pure. Null =
 * no fill area to extrude (Line, degenerate dims): the seam's flat degrade.
 */
function shapeGeomOf(obj) {
  const t = String(obj.type || '').toLowerCase();
  const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  if (t === 'rect') {
    return { type: 'rect', w: num(obj.width), h: num(obj.height), rx: num(obj.rx), ry: num(obj.ry) };
  }
  if (t === 'circle') {
    return {
      type: 'circle',
      radius: num(obj.radius),
      startAngle: obj.startAngle == null ? 0 : num(obj.startAngle),
      endAngle: obj.endAngle == null ? 360 : num(obj.endAngle),
      ccw: !!obj.counterClockwise,
    };
  }
  if (t === 'ellipse') return { type: 'ellipse', rx: num(obj.rx), ry: num(obj.ry) };
  if (t === 'triangle') return { type: 'triangle', w: num(obj.width), h: num(obj.height) };
  if (t === 'polygon' || t === 'polyline') {
    // Polyline extrudes as its closed fill region — the same auto-close a
    // canvas fill performs (winding machinery downstream needs loops).
    const pts = Array.isArray(obj.points)
      ? obj.points.map((pt) => ({ x: num(pt && pt.x), y: num(pt && pt.y) }))
      : [];
    if (pts.length < 3) return null;
    return {
      type: 'polygon',
      points: pts,
      ox: num(obj.pathOffset && obj.pathOffset.x),
      oy: num(obj.pathOffset && obj.pathOffset.y),
    };
  }
  if (t === 'path') {
    if (!Array.isArray(obj.path) || !obj.path.length) return null;
    return {
      type: 'path',
      path: obj.path,
      ox: num(obj.pathOffset && obj.pathOffset.x),
      oy: num(obj.pathOffset && obj.pathOffset.y),
    };
  }
  return null; // 'line' has no fill area; unknown shapes degrade flat
}

/** U3 — alpha-weighted mean color of a raster (32×32 downsample): the honest
 * face tone for an UNTINTED multicolor icon. Null on blank/error. */
function sampleDominantColor(canvas) {
  try {
    const S = 32;
    const cv = document.createElement('canvas');
    cv.width = S;
    cv.height = S;
    const x = cv.getContext('2d', { willReadFrequently: true });
    x.drawImage(canvas, 0, 0, S, S);
    const d = x.getImageData(0, 0, S, S).data;
    let r = 0;
    let g = 0;
    let b = 0;
    let a = 0;
    for (let i = 0; i < d.length; i += 4) {
      const w = d[i + 3];
      if (w < 16) continue;
      r += d[i] * w;
      g += d[i + 1] * w;
      b += d[i + 2] * w;
      a += w;
    }
    if (!a) return null;
    const hex = (v) => Math.round(v / a).toString(16).padStart(2, '0');
    return `#${hex(r)}${hex(g)}${hex(b)}`;
  } catch (_) {
    return null;
  }
}

/**
 * U3 COLOR LAW — the non-text face/walls base color:
 *   icon  — the TINT is the icon's color identity (obj.iconColor, the
 *           engine's own effectCtxFor law); untinted multicolor SVGs sample
 *           their raster (never fabric's meaningless image `fill` default).
 *   shape — solid fill string → itself; gradient → the ramp's MID blend
 *           (faces carry the real ramp via faceFill, walls read the middle
 *           of the material — the text law verbatim); hollow-parked recipe
 *           (fill-hostage law) when the live fill is blanked.
 */
function objectMaterialColorOf(obj, kind, faceFill, midColorOf, baseCanvas) {
  if (kind === 'icon') {
    if (typeof obj.iconColor === 'string' && obj.iconColor) return obj.iconColor;
    return sampleDominantColor(baseCanvas) || '#3b82f6';
  }
  if (faceFill && Array.isArray(faceFill.stops) && faceFill.stops.length >= 2 && midColorOf) {
    try { return midColorOf(faceFill.stops); } catch (_) { /* fall through */ }
  }
  const f = obj.fill;
  if (typeof f === 'string' && f && f !== 'transparent') return f;
  const stop = f && f.colorStops && f.colorStops[0];
  if (stop && typeof stop.color === 'string') return stop.color;
  const parked = obj.data && obj.data.hollowParked && obj.data.hollowParked.fill;
  if (typeof parked === 'string' && parked && parked !== 'transparent') return parked;
  if (parked && typeof parked === 'object') {
    if (typeof parked.color === 'string' && parked.color) return parked.color;
    const s0 = Array.isArray(parked.stops) && parked.stops[0];
    if (s0 && typeof s0.color === 'string') return s0.color;
  }
  return '#3b82f6';
}

/**
 * Per-display-line per-char fill overrides for the bake (Emphasize runs,
 * phonics colors), or null when the object carries none. Reads through
 * fabric's own style resolution (_getStyleDeclaration maps wrapped display
 * lines to logical style indices via the Textbox _styleMap), so the array
 * lines up with obj.textLines — the exact lines handed to the bake. Only
 * string fills count: a per-char gradient override (not constructible from
 * our UI) falls back to the base material.
 */
function charFillsOf(obj, lines) {
  if (!obj.styles || typeof obj._getStyleDeclaration !== 'function') return null;
  try {
    if (typeof obj.styleHas === 'function' && !obj.styleHas('fill')) return null;
  } catch (_) { return null; }
  let any = false;
  const out = lines.map((line, li) => {
    const n = obj._textLines && obj._textLines[li]
      ? obj._textLines[li].length
      : Array.from(String(line)).length;
    const arr = new Array(n).fill(null);
    for (let ci = 0; ci < n; ci++) {
      let decl = null;
      try { decl = obj._getStyleDeclaration(li, ci); } catch (_) { decl = null; }
      const f = decl && decl.fill;
      if (typeof f === 'string' && f && f !== 'transparent') {
        arr[ci] = f;
        any = true;
      }
    }
    return arr;
  });
  return any ? out : null;
}

// Style keys whose change splits a fillText draw run in fabric's renderer
// (utils hasStyleChanged, forTextSpans=false — mirrored here so the traced
// runs are the EXACT strings fabric shapes in one fillText call).
const RUN_SPLIT_KEYS = ['fill', 'stroke', 'strokeWidth', 'fontSize', 'fontFamily',
  'fontWeight', 'fontStyle', 'deltaY'];

/**
 * Snapshot the live Textbox's OWN layout for the traced 3D pipeline
 * (GlyphTrace lane, 2026-07-20): per display line, fabric's alignment offset
 * (_getLineLeftOffset — center/right/justify variants), the baseline law
 * (Text._renderTextCommon + _renderChars: baseline = Σ heightOfLine(<li) +
 * heightOfLineImpl(li) × (1 − _fontSizeFraction), y-down from the box top),
 * and the DRAW RUNS fabric's own renderer would issue — the whole line in
 * one run when it is style-free with charSpacing 0 (fabric's shortCut), else
 * split exactly where _renderChars splits: every char under charSpacing,
 * after each space/tab under justify, and at any style change. Run-level
 * tracing is what makes SCRIPT fonts survive 3D-ification: the browser
 * shapes each run whole (contextual alternates, connecting strokes — the
 * Pacifico 'gt' swash), and fabric's 2D view is drawn from those same runs,
 * so tracing them reproduces the exact letterforms the owner sees. Pen
 * positions come from __charBounds (kerning, charSpacing and justify
 * space-fattening pre-baked by fabric's measurement; the kernedWidth−width
 * pen shift applied at run starts, fabric's own law). Each run carries its
 * RESOLVED font stack (getCompleteStyleDeclaration — the resolution the 2D
 * renderer draws with) plus per-char pens for the curated fallback.
 *
 * Returns null (→ the curated whole-bake pipeline, pre-lane behavior) for
 * layouts this snapshot does not model: text-on-path (per-glyph rotation)
 * and RTL direction (mirrored pen law) — and on any read error: the
 * snapshot is an upgrade, never a crash.
 */
export function buildTextLayoutSnapshot(obj, frameW, frameH) {
  try {
    if (!obj || obj.path || obj.direction === 'rtl') return null;
    const linesG = obj._textLines;
    const bounds = obj.__charBounds;
    if (!Array.isArray(linesG) || !Array.isArray(bounds) || !bounds.length) return null;
    if (typeof obj.getCompleteStyleDeclaration !== 'function'
      || typeof obj.getHeightOfLine !== 'function') return null;
    const implOf = (li) => (typeof obj.getHeightOfLineImpl === 'function'
      ? obj.getHeightOfLineImpl(li)
      : obj.getHeightOfLine(li) / (obj.lineHeight || 1));
    const fsFraction = Number(obj._fontSizeFraction) || 0.222;
    const isJustify = String(obj.textAlign || '').includes('justify');
    const reSpaceTab = /[ \t]/;
    const w = Number(obj.width) || 0;
    const h = Number(obj.height) || 0;
    const fw = Number(frameW) || w;
    const fh = Number(frameH) || h;
    const penOf = (b) => (b ? (b.left || 0) + ((b.kernedWidth || 0) - (b.width || 0)) : 0);
    const lines = [];
    let lineTops = 0;
    for (let li = 0; li < linesG.length; li++) {
      const graphemes = linesG[li] || [];
      const lineBounds = bounds[li] || [];
      const baseline = lineTops + implOf(li) * (1 - fsFraction);
      lineTops += obj.getHeightOfLine(li);
      const left = typeof obj._getLineLeftOffset === 'function' ? obj._getLineLeftOffset(li) : 0;
      const runs = [];
      const styleAt = (ci) => obj.getCompleteStyleDeclaration(li, ci) || {};
      const rawAt = (ci) => {
        try { return obj._getStyleDeclaration(li, ci) || null; } catch (_) { return null; }
      };
      const pushRun = (s, e /* inclusive */) => {
        const st = styleAt(s);
        const raw = rawAt(s);
        const chars = [];
        // fabric's pen-advance law for the drawn box: first char contributes
        // width, the rest kernedWidth — the run's measured width pins the
        // traced advance (glyphTrace advance correction).
        let advance = 0;
        for (let ci = s; ci <= e; ci++) {
          chars.push({ ch: graphemes[ci], penX: penOf(lineBounds[ci]) });
          const b = lineBounds[ci] || {};
          advance += ci === s ? (b.width || 0) : (b.kernedWidth || 0);
        }
        runs.push({
          text: graphemes.slice(s, e + 1).join(''),
          penX: penOf(lineBounds[s]),
          width: advance,
          fontFamily: st.fontFamily || obj.fontFamily || 'sans-serif',
          fontWeight: st.fontWeight ?? obj.fontWeight ?? 'normal',
          fontStyle: st.fontStyle || obj.fontStyle || 'normal',
          fontSize: Number(st.fontSize) || Number(obj.fontSize) || 100,
          // Render parity: fabric applies only the RAW per-char deltaY
          // (Text._renderChar), never an object-level value.
          deltaY: (raw && Number(raw.deltaY)) || 0,
          // Override color = the RAW per-char fill only (a resolved fill
          // would dye every glyph into an override block and evict the base
          // block's gradient/texture recipe — same rule as charFillsOf).
          fill: raw && typeof raw.fill === 'string' && raw.fill ? raw.fill : null,
          chars,
        });
      };
      if (!graphemes.length) {
        lines.push({ left, baseline, runs });
        continue;
      }
      const shortCut = !isJustify && (obj.charSpacing || 0) === 0
        && typeof obj.isEmptyStyles === 'function' && obj.isEmptyStyles(li);
      if (shortCut) {
        pushRun(0, graphemes.length - 1);
      } else {
        let start = 0;
        let prevStyle = styleAt(0);
        for (let ci = 0; ci < graphemes.length; ci++) {
          let breakHere = ci === graphemes.length - 1 || (obj.charSpacing || 0) !== 0;
          if (isJustify && !breakHere && reSpaceTab.test(graphemes[ci])) breakHere = true;
          if (!breakHere) {
            const next = styleAt(ci + 1);
            for (const key of RUN_SPLIT_KEYS) {
              if (prevStyle[key] !== next[key]) { breakHere = true; break; }
            }
            prevStyle = next;
          }
          if (breakHere) {
            pushRun(start, ci);
            start = ci + 1;
            if (ci + 1 < graphemes.length) prevStyle = styleAt(ci + 1);
          }
        }
      }
      lines.push({ left, baseline, runs });
    }
    if (!lines.length) return null;
    return {
      // frame = the rect the seam's objW/objH registration describes (the
      // width/height box grown by the stroke halo, centered): x0/y0 sit at
      // the frame's top-left relative to the box's own top-left, y-down.
      frame: { w: fw, h: fh, x0: -(fw - w) / 2, y0: -(fh - h) / 2 },
      lines,
    };
  } catch (_) {
    return null;
  }
}

/** Outward reach (object units) of the Outline effect's visible band —
 * strokeWidth/2 for both positions (outside doubles the width and hides the
 * inner half under the fill; centered splits it). 0 when outline isn't the
 * active stroke-slot effect. The rim underlay and bounds() share this. */
function outlineReachOf(obj) {
  // Dual-read bag (universal-effects U1): data.effects ?? data.textEffects.
  // The native-stroke check below keeps this 0 for icon Outline (fx band, no
  // native stroke) — the rim is a native-stroke re-expression by design.
  const bag = obj?.data?.effects ?? obj?.data?.textEffects;
  if (!obj || bag?.stroke?.id !== 'outline') return 0;
  if (!obj.stroke) return 0;
  const sw = Number(obj.strokeWidth) || 0;
  return sw > 0 ? sw / 2 : 0;
}

/** Any CSS color (or a fabric Gradient's first stop) → [r, g, b]. */
function rimRgbOf(paint) {
  let css = typeof paint === 'string' ? paint : paint?.colorStops?.[0]?.color;
  if (!css || typeof css !== 'string') css = '#ffffff';
  const cv = document.createElement('canvas');
  cv.width = cv.height = 1;
  const x = cv.getContext('2d', { willReadFrequently: true });
  x.fillStyle = '#000000';
  x.fillStyle = css; // invalid strings keep black
  x.fillRect(0, 0, 1, 1);
  const d = x.getImageData(0, 0, 1, 1).data;
  return [d[0], d[1], d[2]];
}

/**
 * OUTLINE ON 3D (Tier 3): a flat rim in the outline color drawn UNDER the
 * baked raster — the die-cut sticker-edge read of the 2D Outline effect,
 * following the object's stroke props live (the seam hash covers stroke, so
 * every outline tweak re-bakes and re-rims). Mechanics:
 *   1. pad the bake canvas by the rim reach (the camera's fit margin is only
 *      ~4% — a maxed outline would clip at the canvas edge);
 *   2. close die-cut pockets on a COPY of the alpha (same closing radius law
 *      as the 2D border band — the rim must bridge the pocket above an 'i',
 *      not ring it) — gradient outlines keep their first stop (documented);
 *   3. two-pass 3-4 chamfer distance transform outward from the silhouette —
 *      O(pixels), radius-independent, ~40-80 ms on a screen-size bake;
 *   4. composite the ring (1px soft outer edge) via destination-over.
 * Returns { canvas, padPx } — the padded canvas (or the input untouched when
 * the reach is sub-pixel).
 */
function applyRimUnderlay(canvas, { reachPx, rgb, closeRadiusPx, bounds }) {
  if (!(reachPx >= 0.75)) return { canvas, padPx: 0 };
  // Pad cap: the padded result must stay inside the seam's RUNTIME stash
  // bounds or runAsyncUpgrade/fxPrimeExport would refuse to stash it and the
  // render would re-bake forever. MODE-AWARE since the print300 lane: the
  // seam hands its own bounds through env.renderBounds (export primes run at
  // the transient export budget — sizing an export-scale pad against the
  // LIVE caps used to zero the rim on big print bakes). Only bakes already
  // AT the resolution cap can clamp; the drawn ring shrinks with the pad so
  // it thins at the cap instead of clipping flat. The area term solves
  // (W+2p)(H+2p) <= area for p.
  const caps = getRenderCaps();
  const capSide = (bounds && bounds.side) || caps.side;
  const capArea = (bounds && bounds.area) || caps.area;
  const wantPad = Math.ceil(reachPx) + 2;
  const sideRoom = Math.floor((capSide - Math.max(canvas.width, canvas.height)) / 2);
  const wSum = canvas.width + canvas.height;
  const areaRoom = Math.floor(
    (Math.sqrt(wSum * wSum + 4 * Math.max(0, capArea - canvas.width * canvas.height)) - wSum) / 4,
  );
  const pad = Math.max(0, Math.min(wantPad, sideRoom, areaRoom));
  if (pad < 2) return { canvas, padPx: 0 };
  const drawReachPx = Math.min(reachPx, pad - 1.5);
  const W = canvas.width + pad * 2;
  const H = canvas.height + pad * 2;
  const out = document.createElement('canvas');
  out.width = W;
  out.height = H;
  const ctx = out.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(canvas, pad, pad);
  const img = ctx.getImageData(0, 0, W, H);
  const d = img.data;
  // Ring source alpha — closed so the band bridges die-cut pockets.
  let srcAlpha = null;
  if (closeRadiusPx > 0) {
    const copy = new ImageData(new Uint8ClampedArray(d), W, H);
    try {
      bridgeDiecutPockets(copy, W, H, closeRadiusPx);
      srcAlpha = copy.data;
    } catch (_) { srcAlpha = null; }
  }
  const src = srcAlpha || d;
  // 3-4 chamfer DT (orthogonal 3, diagonal 4 → distances in 1/3 px units).
  const INF = 1e9;
  const n = W * H;
  const dist = new Float32Array(n);
  for (let i = 0; i < n; i++) dist[i] = src[i * 4 + 3] >= 128 ? 0 : INF;
  for (let y = 0; y < H; y++) {
    const row = y * W;
    for (let x = 0; x < W; x++) {
      const i = row + x;
      let v = dist[i];
      if (v === 0) continue;
      if (x > 0 && dist[i - 1] + 3 < v) v = dist[i - 1] + 3;
      if (y > 0) {
        const up = i - W;
        if (dist[up] + 3 < v) v = dist[up] + 3;
        if (x > 0 && dist[up - 1] + 4 < v) v = dist[up - 1] + 4;
        if (x < W - 1 && dist[up + 1] + 4 < v) v = dist[up + 1] + 4;
      }
      dist[i] = v;
    }
  }
  for (let y = H - 1; y >= 0; y--) {
    const row = y * W;
    for (let x = W - 1; x >= 0; x--) {
      const i = row + x;
      let v = dist[i];
      if (v === 0) continue;
      if (x < W - 1 && dist[i + 1] + 3 < v) v = dist[i + 1] + 3;
      if (y < H - 1) {
        const dn = i + W;
        if (dist[dn] + 3 < v) v = dist[dn] + 3;
        if (x < W - 1 && dist[dn + 1] + 4 < v) v = dist[dn + 1] + 4;
        if (x > 0 && dist[dn - 1] + 4 < v) v = dist[dn - 1] + 4;
      }
      dist[i] = v;
    }
  }
  const ring = ctx.createImageData(W, H);
  const rd = ring.data;
  const reach3 = drawReachPx * 3;
  for (let i = 0; i < n; i++) {
    const dv = dist[i];
    if (dv > reach3 + 3) continue; // beyond the band (+1px soft edge)
    const a = dv <= reach3 ? 255 : Math.round(255 * (1 - (dv - reach3) / 3));
    if (a <= 0) continue;
    const o = i * 4;
    rd[o] = rgb[0];
    rd[o + 1] = rgb[1];
    rd[o + 2] = rgb[2];
    rd[o + 3] = a;
  }
  const ringCv = document.createElement('canvas');
  ringCv.width = W;
  ringCv.height = H;
  ringCv.getContext('2d').putImageData(ring, 0, 0);
  ctx.globalCompositeOperation = 'destination-over';
  ctx.drawImage(ringCv, 0, 0);
  ctx.globalCompositeOperation = 'source-over';
  return { canvas: out, padPx: pad };
}

registerFx({
  id: 'extrude',
  // v2: composed-bevel edge rounding (consumes:['bevel']) + liveExtrude's
  // self-intersection caps / adaptive bevel segments — high bevelRounding
  // values render smoother than v1 at the same params.
  // v3 (2026-07-18, tessellation lane): error-driven curve tessellation
  // (chord error ≤ 0.25 output px), bevel profile segments scaled by output
  // px (cap 12), creased-normal smoothing on letter walls, output-scaled
  // plate contours (lib/text3d/tessellation.js). Same params render with
  // smooth curves and a continuous specular sweep where v2 showed facets at
  // zoom/print scale — a deliberate visual change at identical params.
  // 2026-07-19 (light-shape-finish): lightMode 'poster' added WITHOUT a bump
  // — old params default to 'custom' and render byte-identically.
  // v4 (2026-07-19, dome-parity lane): the composed bevel maps WIDTH → 3D
  // bevelSize (thickness scaled alongside) and SHAPE → profile (smooth =
  // quarter-round, chisel = straight 1-segment chamfer); depth stays
  // steepness. Same-params renders with a composed bevel change (thickness
  // now rides width × depth instead of depth alone — liveExtrude documents
  // the mapping); bevelRounding-only stacks keep depth=width coupling so
  // explorer presets stay in-family. sideFill added (walls/back material
  // override) — additive, absent key = old JSON = old render.
  // v5 (2026-07-20, GlyphTrace lane): same params now render with the TRUE
  // editor fonts — glyph outlines traced from the browser's own rendering
  // and laid out on fabric's exact 2D metrics (kerning/charSpacing/align/
  // justify/per-char font runs/deltaY), replacing the curated-TTF remap
  // that collapsed most families to Gilroy. Front-face registration now
  // maps the OBJECT BOX (not the ink stretched to it), so glyphs land at
  // their true 2D size and position. A deliberate, owner-requested visual
  // change at identical params ("maintain the font(s) originally chosen in
  // the 2d text"); the curated pipeline remains solely as trace-failure /
  // no-snapshot fallback.
  // T1 (2026-07-22, Depth Everywhere): volumeStyle/volumeRound/volumeBack/
  // volumeMaterial added WITHOUT a bump — the keys are stored only while a
  // volume style is active (noise-drop law in sanitize), so every pre-T1
  // params object keeps its exact JSON, its seam hash and its byte-identical
  // render (probe-t1 cell 1 pins this against a pre-edit capture).
  version: 5,
  kind: 'source',
  sanitize,
  isEmpty: (p) => !(p.depth > 0),
  bounds,
  // 'stickerShape' (sticker-shapes lane, 2026-07-20): a 2D badge drawn under
  // a 3D bake would misregister (the bake owns its own projected mapping)
  // and its spill would inflate every margin for pixels the bake never
  // shows. Consumed like the 2D bevel: the step stays in data.fx untouched
  // and returns the moment 3D drops to 0. The 3D-native badge remains the
  // extruded "Sticker plate".
  // 'contourBand' (U3): a 2D band stroked around the FLAT silhouette would
  // misregister on the projected 3D raster the same way. Consumed = the 3D
  // body renders SOLID in the object's color while extrude is active (the
  // exact composition text hollow/trace already read as under 3D — the
  // recipe color, solid letters); drop 3D to 0 and the band runs live again
  // untouched. Text never carries this step (its bands are native props), so
  // text renders are byte-identical. Debt, documented: icon Outline's band
  // is not re-expressed as a rim under 3D (the text rim reads NATIVE stroke
  // props, which fx bands don't have).
  consumes: ['bevel', 'stickerShape', 'contourBand'],
  async applyAsync(canvas, env) {
    const obj = env.obj;
    const p = env.params;
    // Composition: a sibling bevel step's depth OWNS the edge rounding while
    // extrude is active (see the header notes). Read straight off data.fx —
    // env.params carries only THIS step's params.
    const bevelStep = (Array.isArray(obj.data?.fx) ? obj.data.fx : [])
      .find((s) => s?.id === 'bevel');
    // 250 range (owner 2026-07-19) — the world-unit self-intersection caps
    // downstream keep oversized values physical on the geometry.
    const bevelDepth = clamp(bevelStep?.params?.depth, 0, 250, 0);
    const bevel01 = bevelDepth > 0 ? bevelDepth : p.bevelRounding;
    // Width + shape parity (dome-parity lane): the sibling step's size and
    // technique ride into the 3D profile; without a sibling, bevelRounding
    // drives depth AND width together (the pre-lane coupled behavior).
    const bevelWidth01 = bevelDepth > 0
      ? clamp(bevelStep?.params?.size, 0, 250, FX_BEVEL_DEFAULTS.size)
      : p.bevelRounding;
    const bevelShape = bevelDepth > 0 && bevelStep?.params?.technique === 'chisel'
      ? 'chisel'
      : 'smooth';
    // FINISH (2026-07-20 debt note): the 2D bevel's `finish` param is NOT
    // mapped into the 3D bake this lane. extrude keeps its OWN archetype Finish
    // (p.archetype — the deliberate 3D material choice, same "the step's own
    // choice wins" rule as textureId). The mapping WOULD be a trivial value
    // remap (satin→satin, glossy→candy-gloss, metal→metal-foil, matte→matte),
    // but wiring it would let a consumed 2D finish silently override the user's
    // 3D Finish chip — so it stays a separate control. Revisit if the two
    // finishes should unify.
    // U3 kind routing: shapes/icons take the silhouetteOf path below; text
    // keeps its exact pre-lane pipeline (lines + layout snapshot).
    const kind = objectKindOf(obj);
    const isObjectKind = kind === 'shape' || kind === 'icon';
    // Wrapped display lines (Textbox re-wraps live); logical fallback for
    // anything without textLines.
    let lines = [];
    if (!isObjectKind) {
      lines = obj.textLines && obj.textLines.length
        ? [...obj.textLines]
        : String(obj.text ?? '').split('\n');
      if (!lines.some((l) => String(l).trim())) return null; // nothing to cut
    }
    // silhouetteOf (U3): the extrusion source input for non-text kinds.
    //   icon  — the sub-pixel traced contours off the U1 silhouette variant
    //           (fxContourOutline, reused whole). Null (blank icon, no
    //           variant, patch bake without remembered contours) = flat
    //           degrade, the seam's documented fallback.
    //   shape — the plain-geometry snapshot; silhouette3d cuts exact
    //           vectors from it inside the bake.
    let silhouette = null;
    if (isObjectKind) {
      if (kind === 'icon') {
        const contours = traceObjectSilhouette(env);
        if (!contours) return null;
        silhouette = { kind: 'icon', contours };
      } else {
        const geom = shapeGeomOf(obj);
        if (!geom) return null; // Line/unknown: no fill area
        silhouette = { kind: 'shape', geom };
      }
    }
    // three.js + opentype live behind this import — never in the boot chunk.
    const { bakeLiveExtrude, gradientMidColor } = await import('../text3d/liveExtrude.js');
    const dim = obj._getTransformedDimensions({ skewX: 0, skewY: 0 });
    const objW = Math.max(1, dim.x / (obj.scaleX || 1));
    const objH = Math.max(1, dim.y / (obj.scaleY || 1));
    // faceFillOf reads data.textFill first (absent on non-text) then the
    // live fabric Gradient — so a gradient-filled SHAPE rides the U1
    // face-texture path for free; icons return null (tint law below).
    const faceFill = faceFillOf(obj);
    // Recipe size basis: fontSize for text, min(w,h) for shapes/icons — the
    // seam's unitFor convention, so world depth ↔ u proportions match
    // bounds() and survive corner-drag resizes.
    const sizeBasis = isObjectKind
      ? Math.max(1, Math.min(objW, objH))
      : (obj.fontSize || 100);
    // VIEWPORT PATCH mode (2026-07-18): env.patchRegion = a sub-rect of the
    // object's raster in OBJECT UNITS (center-origin) to bake at env.reqScale
    // instead of the whole frame. The bake's region option renders exactly
    // that world-rect through the same camera (asymmetric-frustum slice —
    // pixel-identical to the same rows of a full bake). The outline rim needs
    // pixels BEYOND the requested rect (distance transform + pocket closing
    // read the neighborhood), so the bake region is expanded by the rim's
    // reach and the seam crops the guard off after compositing — the wrong
    // rim-along-the-cut band that a sliced silhouette would grow lives
    // entirely inside the cropped guard.
    const patch = env.patchRegion || null;
    const rimReachU0 = outlineReachOf(obj);
    let rimGuardU = 0;
    if (patch && rimReachU0 > 0) {
      const ppuEst = Math.max(0.02, env.reqScale || env.scale || 1);
      const closePx = diecutCloseRadiusPx(rimReachU0 * ppuEst, Math.max(0.01, env.u * ppuEst));
      rimGuardU = rimReachU0 + (closePx + 4) / ppuEst;
    }
    const out = await bakeLiveExtrude({
      lines,
      // U3: non-text kinds hand the bake their silhouette (exact vectors /
      // traced contours) + the object-box frame for front-face registration
      // — the text fields below are then inert.
      silhouette: silhouette ? { ...silhouette, frameW: objW, frameH: objH } : null,
      // GlyphTrace (2026-07-20): fabric's own metrics + per-char styles →
      // the traced TRUE-FONT pipeline. Null (path text, RTL, read failure)
      // = the curated whole-bake pipeline; the fields below then drive it
      // exactly as before.
      layout: isObjectKind ? null : buildTextLayoutSnapshot(obj, objW, objH),
      textAlign: obj.textAlign || 'left',
      fontFamily: obj.fontFamily,
      fontWeight: obj.fontWeight,
      lineHeight: obj.lineHeight || 1.16,
      charSpacingEm: (obj.charSpacing || 0) / 1000,
      // Opaque by 3D law: a live rgba() fill (paint opacity) flattens to its
      // RGB — the walls/plate read the color, never the translucency. The
      // self-fallback keeps exotic legacy strings (named colors) untouched.
      // Non-text kinds ride the U3 color law (tint = the icon's identity;
      // shape solid/gradient-mid/hollow-parked).
      color: (() => {
        const c = isObjectKind
          ? objectMaterialColorOf(obj, kind, faceFill, gradientMidColor, canvas)
          : materialColorOf(obj, faceFill, gradientMidColor);
        return normalizeOpaqueHex(c, c);
      })(),
      faceFill,
      charFills: isObjectKind ? null : charFillsOf(obj, lines),
      fontSize: sizeBasis,
      depth01: p.depth,
      bevel01,
      bevelWidth01,
      bevelShape,
      // Volume style (T1): 'solid' (absent key) routes the exact pre-lane
      // ExtrudeGeometry pipeline — liveExtrude branches ONLY on non-solid, so
      // old params render byte-identically. puffy/pillow swap the geometry
      // producer at the buildLettersGeometry seam; the composed bevel above
      // is then geometrically inert (the volume rim law owns the edge — see
      // FX_EXTRUDE_VOLUME_DEFAULTS notes). volumeMaterial is stored-only this
      // lane ('default' consumed = the face-fill resolution).
      volumeStyle: p.volumeStyle === 'puffy' || p.volumeStyle === 'pillow' ? p.volumeStyle : 'solid',
      volumeRound01: clamp(p.volumeRound, 0, 100, FX_EXTRUDE_VOLUME_DEFAULTS.round),
      volumeBack: p.volumeBack === 'round' ? 'round' : 'flat',
      // VOLUME WORKER CHANNEL (lane VW — the progressive swap): live volume
      // bakes hand liveExtrude a per-object channel token + the seam's
      // landing callback; the worker pool then builds the capped PREVIEW
      // and the full-density mesh concurrently off-thread, and the landing
      // callback force-rebakes through the standard stash path (same hash,
      // quality marker upgraded — fxRender.scheduleVolumeSwap). Patch bakes
      // ride a SEPARATE preview-only channel: they must never bump the
      // stash channel's generation (that would cancel its in-flight full
      // solve), and their region tier never swaps. Exports pass null —
      // they bake full density in the primed bracket exactly as before.
      volumeUpgrade: (p.volumeStyle === 'puffy' || p.volumeStyle === 'pillow') && !env.exporting
        ? (patch
            ? {
                channel: obj.__fxVolumePatchChannel || (obj.__fxVolumePatchChannel = {}),
                previewOnly: true,
              }
            : (typeof env.scheduleSourceRefresh === 'function'
                ? {
                    channel: obj.__fxVolumeChannel || (obj.__fxVolumeChannel = {}),
                    onLanded: env.scheduleSourceRefresh,
                  }
                : null))
        : null,
      archetype: p.archetype,
      textureId: p.textureId,
      sideFill: p.sideFill || null,
      lighting: p.lightPreset,
      // Poster light (2026-07-19): resolved at BAKE time off the live canvas;
      // the seam's hash carries these values for opted-in stacks, so a light
      // edit re-bakes through the standard stale-stash → scheduleAsync path.
      posterLight: p.lightMode === 'poster' ? getPosterLightFor(obj) : null,
      rx: p.rx,
      ry: p.ry,
      plate: p.plate || null,
      // The projected front-face text should land at the object's on-screen
      // resolution: object width (units) × device px per unit. env.reqScale
      // (the scale the seam was ASKED for) — not env.scale, which drops when
      // the margin-padded base raster hits a cap the 3D frame wouldn't (the
      // bake re-cuts vectors; it never resamples the base).
      targetFrontWidthPx: Math.max(64, objW * (env.reqScale || env.scale)),
      // Volume density governor (freeze fix, 2026-07-22): live volume
      // geometry (Poisson/levelSet WASM) is synchronous CPU whose cost
      // scales with pxPerWorld² — exports keep full density, live bakes
      // are capped inside buildVolumeGeometry via this flag.
      exporting: !!env.exporting,
      // Mode-aware output budget (print300): live renders keep the live
      // stash caps; export primes may size up to the transient export
      // budget. renderer3d tiles the GL work past its own ceiling.
      budget: env.renderBounds
        ? { side: env.renderBounds.side, area: env.renderBounds.area }
        : null,
      // Viewport patch: bake ONLY this front-width-fraction slice of the
      // frame (object units / objW — isotropic, centered on the front face).
      region: patch
        ? {
            x0: (patch.x0 - rimGuardU) / objW,
            y0: (patch.y0 - rimGuardU) / objW,
            x1: (patch.x1 + rimGuardU) / objW,
            y1: (patch.y1 + rimGuardU) / objW,
          }
        : null,
    });
    if (!out) return null;
    // Object-unit mapping: projected front-face width ↔ the object's width.
    // For region bakes frontWidthPx is the VIRTUAL full frame's — the ppu is
    // identical to what the full bake at this target would deliver.
    const pxPerUnit = out.frontWidthPx / objW;
    // Outline → flat rim around the silhouette (Tier 3; see applyRimUnderlay).
    let resultCanvas = out.canvas;
    const rimReachU = rimReachU0;
    if (rimReachU > 0) {
      try {
        const reachPx = rimReachU * pxPerUnit;
        resultCanvas = applyRimUnderlay(out.canvas, {
          reachPx,
          rgb: rimRgbOf(obj.stroke),
          closeRadiusPx: diecutCloseRadiusPx(reachPx, Math.max(0.01, env.u * pxPerUnit)),
          bounds: env.renderBounds || null,
        }).canvas;
      } catch (_) { /* rim is decoration — the bake must land regardless */ }
    }
    // Lane VW: the volume build's quality tier ('preview' | 'full' | 'sync',
    // null for solid) — rides the stash as its swap marker (fxRender).
    const volumeQuality = (out.stats && out.stats.volume && out.stats.volume.quality) || null;
    if (patch) {
      if (!out.regionPx) return null;
      // The delivered canvas covers regionPx of the virtual frame, grown by
      // the (symmetric) rim pad. Map its exact object rect: the seam does
      // ONE final crop back to the requested draw rect — sub-pixel-exact
      // registration is this arithmetic, nothing else.
      const padPx = (resultCanvas.width - (out.regionPx.x1 - out.regionPx.x0)) / 2;
      const x0 = (out.regionPx.x0 - padPx - out.frontCenterX) / pxPerUnit;
      const y0 = (out.regionPx.y0 - padPx - out.frontCenterY) / pxPerUnit;
      return {
        canvas: resultCanvas,
        volumeQuality,
        patchRect: {
          x0,
          y0,
          x1: x0 + resultCanvas.width / pxPerUnit,
          y1: y0 + resultCanvas.height / pxPerUnit,
        },
      };
    }
    return {
      canvas: resultCanvas,
      volumeQuality,
      halfW: resultCanvas.width / 2 / pxPerUnit,
      halfH: resultCanvas.height / 2 / pxPerUnit,
      // Anchor: the projected front-face center sits on the object's center.
      // The rim pad is symmetric, so the canvas center is unmoved (ax/ay are
      // center-relative and need no pad term).
      ax: (out.frontCenterX - out.canvas.width / 2) / pxPerUnit,
      ay: (out.frontCenterY - out.canvas.height / 2) / pxPerUnit,
    };
  },
});
